import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, IconButton, TextField, Stack, Grid, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Chip, InputAdornment, FormControl, InputLabel, Select, MenuItem, Tooltip, Popover, Divider, RadioGroup, FormControlLabel, Radio, Paper } from "@mui/material";
import { Assessment as AssessmentIcon, Person as PersonIcon, Close as CloseIcon, People, PersonPinOutlined, AccessTime, EventBusy, Search as SearchIcon, RestartAlt, FileDownload as FileDownloadIcon, PictureAsPdf as PdfIcon, TableChart as ExcelIcon, LocationOn, LocationOff, History as HistoryIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from "date-fns";
import { useNavigate } from 'react-router-dom';
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from './../../../services/endpoints';
import Loader from "../../../common/spinner";
import MyAttendance from './../../../components/common/myAttendance';
import StatsCard from '../../../components/common/StatsCard';

function HRAttendanceManagement() {
    const navigate = useNavigate();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const orgId = userData?.organization_id;

    const initialFilters = {
        searchText: "",
        status: "All",
        location: "All",
        checkInOffice: "All",
        checkOutOffice: "All"
    };

    // --- State Management ---
    const [view] = useState("report");
    const [openMyAttendance, setOpenMyAttendance] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [employeeRecords, setEmployeeRecords] = useState([]);
    const [locations, setLocations] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [anchorEl, setAnchorEl] = useState(null);
    const [downloadFormat, setDownloadFormat] = useState("");

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [todayAttendanceOverview, setTodayAttendanceOverview] = useState({
        totalEmployees: 0,
        presentToday: 0,
        absentToday: 0,
        lateArrivals: 0,
        leaveToday: 0
    });

    const openDownload = Boolean(anchorEl);
    const [openMonthlyDialog, setOpenMonthlyDialog] = useState(false);
    const [monthlyParams, setMonthlyParams] = useState({
        month: new Date().getMonth() + 1, // Default to current month (1-12)
        year: new Date().getFullYear(),
        format: ""
    });

    const months = [
        { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
        { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
        { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
        { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
    ];

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

    // --- Helper Functions ---
    const handleOpenMyAttendance = () => setOpenMyAttendance(true);
    const handleCloseMyAttendance = () => setOpenMyAttendance(false);

    const formatTime = (dateString) => {
        if (!dateString || dateString === "-") return "N/A";
        try {
            return format(new Date(dateString), "hh:mm a");
        } catch {
            return "N/A";
        }
    };

    // --- API Calls ---
    const fetchAttendanceOverview = useCallback(async () => {
        if (!orgId) return;
        try {
            const response = await getData(API_ENDPOINTS.getDailyAttendanceOverview(orgId));
            if (response?.status === 'SUCCESS') {
                setTodayAttendanceOverview(response?.message);
            }
        } catch (error) {
            return null;
        }
    }, [orgId]);

    const fetchEmployeeAttendance = useCallback(async (date) => {
        if (!orgId) return;
        setLoading(true);
        try {
            const formattedDate = format(date, "MM/dd/yyyy");
            const response = await getData(API_ENDPOINTS.getHRAttendanceEmployeesByDateAndOrgId(formattedDate, orgId));
            if (response?.status === 'SUCCESS') {
                setEmployeeRecords(response.message.employeeRecords || []);
            }
        } catch (error) {
            // Update state BEFORE returning
            setEmployeeRecords([]);
            return null;
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    const fetchLocations = useCallback(async () => {
        if (!orgId) return;
        try {
            const response = await getData(API_ENDPOINTS.fetchLocationsByOrganizationId(orgId));
            if (response?.status === 'SUCCESS') {
                setLocations(response.message);
            }
        } catch (error) {
            return null;
        }
    }, [orgId]);

    useEffect(() => {
        fetchAttendanceOverview();
        fetchEmployeeAttendance(selectedDate);
        fetchLocations();
    }, [orgId, selectedDate, fetchAttendanceOverview, fetchEmployeeAttendance, fetchLocations]);

    // --- Filter Logic ---
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(0); // Reset to first page on filter change
    };

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setPage(0);
    };

    const filteredRecords = useMemo(() => {
        return employeeRecords.filter((row) => {
            const matchesSearch =
                row.name.toLowerCase().includes(filters.searchText.toLowerCase()) ||
                row.emp_id.toLowerCase().includes(filters.searchText.toLowerCase());

            let matchesStatus = true;
            if (filters.status !== "All") {
                // if (filters.status === "Present") {
                //     matchesStatus = row.status === "Present";
                // } else {
                // }
                matchesStatus = row.status === filters.status;
            }

            const matchesLocation = filters.location === "All" || row.location === filters.location;

            const matchesCheckIn = filters.checkInOffice === "All" ||
                row.office_premises?.check_in?.in_office === filters.checkInOffice;

            const matchesCheckOut = filters.checkOutOffice === "All" ||
                row.office_premises?.check_out?.in_office === filters.checkOutOffice;

            return matchesSearch && matchesStatus && matchesLocation && matchesCheckIn && matchesCheckOut;
        });
    }, [employeeRecords, filters]);

    // --- Download Logic ---
    const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);
    const handleDownloadClose = () => {
        setAnchorEl(null);
        setDownloadFormat("");
    };

    const startDownload = async () => {
        if (!orgId || !downloadFormat) return;

        const formattedDate = format(selectedDate, "yyyy-MM-dd");
        const locationObj = locations.find(loc => loc.location_name === filters.location);
        const locationId = locationObj ? locationObj.id : "All";

        const mapOfficeLabel = (val) => {
            if (val === "yes") return "In Office";
            if (val === "no") return "Outside";
            return "All";
        };

        try {
            setLoading(true);
            const params = new URLSearchParams({
                date: formattedDate,
                status: filters.status,
                organization_id: orgId,
                location_id: locationId,
                checkin_location: mapOfficeLabel(filters.checkInOffice),
                checkout_location: mapOfficeLabel(filters.checkOutOffice)
            }).toString();

            const baseUrl = downloadFormat === "pdf"
                ? API_ENDPOINTS.getDayWiseAttendancePDF(orgId)
                : API_ENDPOINTS.getDayWiseAttendanceExcel(orgId);

            const finalUrl = `${baseUrl}?${params}`;
            const response = await getData(finalUrl, { responseType: 'blob' });

            const blob = new Blob([response], {
                type: downloadFormat === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Attendance_Report_${formattedDate}.${downloadFormat === "pdf" ? "pdf" : "xlsx"}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
            handleDownloadClose();
        }
    };

    const handleMonthlyDownload = async () => {
        if (!orgId || !monthlyParams.format) return;

        setLoading(true);
        try {
            const { month, year, format } = monthlyParams;
            const isPdf = format === "pdf";

            const path = isPdf
                ? API_ENDPOINTS.downloadMonthlyAttendancePDF({})
                : API_ENDPOINTS.downloadMonthlyAttendanceExcel({});

            const finalUrl = `${path}?month=${month}&year=${year}&organization_id=${orgId}`;

            const response = await getData(finalUrl, { responseType: 'blob' });

            const blob = new Blob([response], {
                type: isPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Monthly_Attendance_${month}_${year}.${isPdf ? "pdf" : "xlsx"}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setOpenMonthlyDialog(false);
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    };

    // --- Sub-components ---

    return (
        <Box sx={{ p: 1 }}>
            {loading && <Loader />}

            {/* Header Section */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                mb={3}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>Attendance Management</Typography>
                    <Typography variant="body2" color="text.secondary">Monitor and manage employee daily records</Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                        variant={view === "report" ? "contained" : "outlined"}
                        onClick={() => setOpenMonthlyDialog(true)}
                        startIcon={<AssessmentIcon />}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        fullWidth
                    >
                        Attendance Report
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleOpenMyAttendance}
                        startIcon={<PersonIcon />}
                        sx={{ textTransform: 'none' }}
                        fullWidth
                    >
                        My Attendance
                    </Button>
                </Stack>
            </Stack>

            {/* Stats Overview */}
            <Grid container spacing={3} mb={4}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatsCard title="Total" value={todayAttendanceOverview.totalEmployees} icon={<People />} color="#6366F1" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatsCard title="Present" value={todayAttendanceOverview.presentToday} icon={<PersonPinOutlined />} color="#10B981" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatsCard title="Late" value={todayAttendanceOverview.lateArrivals} icon={<AccessTime />} color="#F59E0B" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatsCard title="Absent" value={todayAttendanceOverview.absentToday} icon={<EventBusy />} color="#EF4444" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatsCard title="Leaves" value={todayAttendanceOverview.leaveToday} icon={<PersonPinOutlined />} color="#3B82F6" /></Grid>
            </Grid>

            {/* Toolbar Section */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h6" fontWeight="700">Records</Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Filter by Date"
                        value={selectedDate}
                        onChange={setSelectedDate}
                        format="dd/MM/yyyy"
                        slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
                    />
                </LocalizationProvider>
            </Stack>

            {/* Filters */}
            <Grid container spacing={2} mb={3} alignItems="center">
                <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                        fullWidth size="small"
                        name="searchText"
                        placeholder="Search Name or ID..."
                        value={filters.searchText}
                        onChange={handleFilterChange}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Location</InputLabel>
                        <Select name="location" value={filters.location} label="Location" onChange={handleFilterChange}>
                            <MenuItem value="All">All Locations</MenuItem>
                            {locations.map((loc) => (<MenuItem key={loc.id} value={loc.location_name}>{loc.location_name}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 1.5 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select name="status" value={filters.status} label="Status" onChange={handleFilterChange}>
                            <MenuItem value="All">All Status</MenuItem>
                            <MenuItem value="Present">Present</MenuItem>
                            <MenuItem value="Absent">Absent</MenuItem>
                            <MenuItem value="LateArrival">Late Arrival</MenuItem>
                            <MenuItem value="Leave">Leave</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Check-in Office</InputLabel>
                        <Select name="checkInOffice" value={filters.checkInOffice} label="Check-in Office" onChange={handleFilterChange}>
                            <MenuItem value="All">Any</MenuItem>
                            <MenuItem value="yes">In Office</MenuItem>
                            <MenuItem value="no">Outside</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Check-out Office</InputLabel>
                        <Select name="checkOutOffice" value={filters.checkOutOffice} label="Check-out Office" onChange={handleFilterChange}>
                            <MenuItem value="All">Any</MenuItem>
                            <MenuItem value="yes">In Office</MenuItem>
                            <MenuItem value="no">Outside</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 1.5 }} sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={handleResetFilters} startIcon={<RestartAlt />} fullWidth sx={{ textTransform: 'none' }}>Reset</Button>
                    <Tooltip title="Export Report">
                        <IconButton onClick={handleDownloadClick} sx={{ border: '1px solid #ddd' }}><FileDownloadIcon color="primary" /></IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {/* Table */}
            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Employee</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Status</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Check-in</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Check-out</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>In-Office IN</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>In-Office OUT</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Prod. Hours</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                            {filteredRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.user_id} hover sx={{
                                    '&:hover': {
                                        bgcolor: '#F1F5F9 !important'
                                    }
                                }}>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{row.emp_id}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Chip label={row.status} size="small" color={row.status === "Present" ? "success" : "error"} variant="outlined" sx={{ width: '110px' }} />
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>{formatTime(row.check_in)}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>{formatTime(row.check_out)}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            {row.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? <LocationOn fontSize="small" color="success" /> : <LocationOff fontSize="small" color="error" />}
                                            <Typography variant="caption">{row.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? 'Yes' : 'No'}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        {row.check_out && row.check_out !== '-' ? (
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                {row.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? <LocationOn fontSize="small" color="success" /> : <LocationOff fontSize="small" color="error" />}
                                                <Typography variant="caption">{row.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? 'Yes' : 'No'}</Typography>
                                            </Stack>
                                        ) : "-"}
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}><Typography variant="body2" fontWeight={700}>{row.total_hours || '0:00'}</Typography></TableCell>
                                    <TableCell sx={{ color: '#1E293B' }} align="center">
                                        <Tooltip title="View Attendance History">
                                            <IconButton size="small" color="primary"><HistoryIcon fontSize="small" onClick={() => navigate(`/hr/employee-attendance-history?id=${row.user_id}&empId=${row.emp_id}&empName=${row.name}`)} /></IconButton>
                                        </Tooltip>
                                        {/* <Tooltip title="Edit Attendance">
                                            <IconButton size="small" color="primary"><EditNoteIcon fontSize="small" /></IconButton>
                                        </Tooltip> */}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredRecords.length === 0 && (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>No records found matching filters.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>
            <TablePagination
                component="div"
                count={filteredRecords.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />

            {/* Modals & Popovers */}
            <Dialog open={openMyAttendance} onClose={handleCloseMyAttendance} fullWidth maxWidth="lg">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="700">My Attendance</Typography>
                    <IconButton onClick={handleCloseMyAttendance}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers><MyAttendance /></DialogContent>
            </Dialog>

            {/* Monthly Attendance Download Dialog */}
            <Dialog open={openMonthlyDialog} onClose={() => setOpenMonthlyDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Download Monthly Attendance Report</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Month</InputLabel>
                                    <Select
                                        value={monthlyParams.month}
                                        label="Month"
                                        onChange={(e) => setMonthlyParams({ ...monthlyParams, month: e.target.value })}
                                    >
                                        {months.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Year</InputLabel>
                                    <Select
                                        value={monthlyParams.year}
                                        label="Year"
                                        onChange={(e) => setMonthlyParams({ ...monthlyParams, year: e.target.value })}
                                    >
                                        {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <FormControl fullWidth size="small">
                            <InputLabel>Select Format</InputLabel>
                            <Select
                                value={monthlyParams.format}
                                label="Select Format"
                                onChange={(e) => setMonthlyParams({ ...monthlyParams, format: e.target.value })}
                            >
                                <MenuItem value="pdf">
                                    <Box display="flex" alignItems="center" gap={1}><PdfIcon sx={{ color: "#ef4444", fontSize: 20 }} /> PDF Document</Box>
                                </MenuItem>
                                <MenuItem value="excel">
                                    <Box display="flex" alignItems="center" gap={1}><ExcelIcon sx={{ color: "#10b981", fontSize: 20 }} /> Excel Spreadsheet</Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={() => setOpenMonthlyDialog(false)} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!monthlyParams.format || loading}
                        startIcon={<FileDownloadIcon />}
                        onClick={handleMonthlyDownload}
                        sx={{ textTransform: 'none' }}
                    >
                        Download
                    </Button>
                </Box>
            </Dialog>

            <Popover
                open={openDownload}
                anchorEl={anchorEl}
                onClose={handleDownloadClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Box sx={{ p: 2, width: 220 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Export Format</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <RadioGroup value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
                        <FormControlLabel value="pdf" control={<Radio size="small" />} label={<Box display="flex" alignItems="center" gap={1}><PdfIcon sx={{ color: "#ef4444" }} /> PDF</Box>} />
                        <FormControlLabel value="excel" control={<Radio size="small" />} label={<Box display="flex" alignItems="center" gap={1}><ExcelIcon sx={{ color: "#10b981" }} /> Excel</Box>} />
                    </RadioGroup>
                    {downloadFormat && <Button fullWidth variant="contained" size="small" sx={{ mt: 2 }} onClick={startDownload}>Download</Button>}
                </Box>
            </Popover>
        </Box>
    );
}

export default HRAttendanceManagement;